import React from 'react'
import LoginFrame, { LoginFrameChildrenProps } from './LoginFrame'
import { AvatarType, contextSigninSubmit, onMethodChange } from '../../types.d'
import { preventDefault } from '../../helpers'
import Avatar from './Avatar'

type LoginMethodFrameProps = {
  recoveryPrompt?: string
  recoveryText?: string
  children: React.ReactElement
  onMethodChange: onMethodChange | string
  avatar: AvatarType
  displayName: string
  email: string
  error?: string | null
} & LoginFrameChildrenProps

export type LoginMethodContentsOnSubmitProps = {
  onSubmit: contextSigninSubmit
  email: string
} & LoginFrameChildrenProps

const LoginMethodFrameContents = ({
  recoveryPrompt,
  recoveryText = 'Try another way',
  children,
  recaptchaLoading,
  recaptchaPromise,
  onMethodChange,
  displayName,
  avatar,
  email,
  error,
}: LoginMethodFrameProps) => (
  <>
    <div className="flex flex-col items-center sm:flex-row">
      <Avatar {...avatar} />
      <div className="text-center sm:text-left min-w-0 flex-1 max-w-full">
        <h3 className="text-xl font-bold tracking-tight text-gray-900 truncate max-w-full">
          {displayName}
        </h3>
        <span className="text-gray-500 truncate block max-w-full">{email}</span>
      </div>
    </div>
    {error && (
      <div
        className="font-medium p-4 mb-4 text-sm text-red-800 rounded-lg bg-red-50"
        role="alert"
      >
        {error}
      </div>
    )}
    {React.Children.map(children, (child) => {
      if (!React.isValidElement(child)) return child

      if (typeof child.type === 'function') {
        return React.cloneElement(child as React.ReactElement<any>, {
          recaptchaPromise,
          recaptchaLoading,
        })
      }

      return child
    })}
    {recoveryPrompt && (
      <p className="text-sm font-light text-gray-500">
        {recoveryPrompt}{' '}
        <a
          href={typeof onMethodChange === 'string' ? onMethodChange : '#'}
          className="font-medium text-primary-700 hover:underline"
          onClick={
            typeof onMethodChange === 'function'
              ? preventDefault(onMethodChange(null))
              : null
          }
        >
          {recoveryText}
        </a>
      </p>
    )}
  </>
)

const LoginMethodFrame = ({
  title,
  recoveryPrompt,
  recoveryText = 'Try another way',
  children,
  loading,
  error,
  onPreviousStep = null,
  onMethodChange,
  displayName,
  avatar,
  email,
  logoUrl = null,
}) => (
  <LoginFrame
    recaptchaSiteKey={null}
    {...{ title, loading, onPreviousStep, logoUrl }}
  >
    <LoginMethodFrameContents
      {...{
        title,
        recoveryPrompt,
        recoveryText,
        children,
        onMethodChange,
        displayName,
        avatar,
        email,
        error,
      }}
    />
  </LoginFrame>
)

export default LoginMethodFrame
